<template>
  <q-page class="eval-hub-page">
    <div class="eval-hub-container">
      <!-- Loading state -->
      <div v-if="loading" class="eval-hub-container">
        <div style="display: flex; align-items: flex-end; justify-content: space-between;">
          <div>
            <q-skeleton type="text" width="200px" dark style="font-size: 1.75rem;" />
            <q-skeleton type="text" width="320px" dark style="margin-top: 4px;" />
          </div>
          <q-skeleton type="rect" width="180px" height="40px" dark style="border-radius: 12px;" />
        </div>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; margin-top: 24px;">
          <q-skeleton v-for="n in 4" :key="n" type="rect" height="72px" dark style="border-radius: 12px;" />
        </div>
        <div style="margin-top: 24px;">
          <q-skeleton type="text" width="180px" dark style="font-size: 1rem; margin-bottom: 12px;" />
          <q-skeleton v-for="n in 3" :key="n" type="rect" height="140px" dark style="border-radius: 16px; margin-bottom: 12px;" />
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="error" style="text-align: center; padding: 48px 16px;">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p style="color: var(--oa-text-muted); margin-top: 12px;">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="eval-hub-header">
          <div>
            <h1 class="eval-hub-title">Evaluaciones</h1>
            <p class="eval-hub-subtitle">
              Sistema unificado de supervision y formacion.
              {{ totalInstrumentCriteria }} criterios de supervision, {{ totalRubricIndicators }} indicadores de formacion.
            </p>
          </div>
          <router-link to="/evaluations/live/new" class="eval-hub-cta">
            <q-icon name="play_arrow" size="18px" />
            Evaluar sesion completa
          </router-link>
        </div>

        <!-- Main grid: 2 columns -->
        <div class="eval-hub-grid">
          <!-- LEFT: Supervision -->
          <div class="eval-hub-left">
            <!-- Section header -->
            <div class="section-header">
              <div class="section-icon" style="background: color-mix(in srgb, var(--oa-tier-cc) 15%, transparent); color: var(--oa-tier-cc);">
                <q-icon name="visibility" size="18px" />
              </div>
              <div>
                <h2 class="section-title">Supervision de Sesiones</h2>
                <p class="section-desc">4 instrumentos en un set unificado por sesion</p>
              </div>
            </div>

            <!-- Flow bar -->
            <div class="flow-bar">
              <span
                v-for="(step, i) in flowSteps"
                :key="step"
                class="flow-step"
                :style="{ color: i === flowSteps.length - 1 ? 'var(--oa-tier-cc)' : 'var(--oa-text-muted)' }"
              >
                <span
                  class="flow-step-num"
                  :style="{
                    background: i === flowSteps.length - 1
                      ? 'color-mix(in srgb, var(--oa-tier-cc) 20%, transparent)'
                      : 'color-mix(in srgb, var(--oa-gold-full) 12%, transparent)',
                    color: i === flowSteps.length - 1 ? 'var(--oa-tier-cc)' : 'var(--oa-gold-dim)',
                  }"
                >{{ i + 1 }}</span>
                <span class="flow-step-label">{{ step }}</span>
                <q-icon
                  v-if="i < flowSteps.length - 1"
                  name="arrow_forward"
                  size="10px"
                  style="color: var(--oa-text-disabled); margin-left: 4px;"
                />
              </span>
            </div>

            <!-- Instruments list -->
            <div class="instrument-list">
              <router-link
                v-for="inst in instruments"
                :key="inst.key"
                :to="`/evaluations/live/new?instrument=${inst.key}`"
                class="instrument-row"
              >
                <div class="instrument-icon-wrap">
                  <q-icon :name="inst.icon" size="18px" />
                </div>
                <div class="instrument-info">
                  <p class="instrument-label">{{ inst.label }}</p>
                  <div class="instrument-meta">
                    <span>{{ inst.scoring }}</span>
                    <span>{{ instrumentStats[inst.key]?.areas ?? 0 }} areas</span>
                    <span>{{ instrumentStats[inst.key]?.criteria ?? 0 }} criterios</span>
                  </div>
                </div>
                <span class="instrument-weight">{{ inst.weight }}</span>
                <q-icon name="chevron_right" size="18px" class="instrument-chevron" />
              </router-link>
            </div>

            <!-- Quick links -->
            <div class="quick-links">
              <router-link
                v-for="link in quickLinks"
                :key="link.href"
                :to="link.href"
                class="quick-link"
              >
                <q-icon :name="link.icon" size="14px" :style="{ color: link.accent }" />
                <span class="quick-link-label">{{ link.label }}</span>
              </router-link>
            </div>
          </div>

          <!-- RIGHT: Formacion / Rubrics -->
          <div class="eval-hub-right">
            <!-- Section header -->
            <div class="section-header">
              <div class="section-icon" style="background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent); color: var(--oa-gold-full);">
                <q-icon name="hexagon" size="18px" />
              </div>
              <div>
                <h2 class="section-title">Rubricas de Formacion</h2>
                <p class="section-desc">Scoring ponderado por dimensiones</p>
              </div>
            </div>

            <!-- Template cards -->
            <div class="template-list">
              <div
                v-for="template in templates"
                :key="template.id"
                class="template-card"
              >
                <!-- Card header -->
                <div class="template-card-header">
                  <h3 class="template-card-name">{{ template.name }}</h3>
                  <div class="template-card-meta">
                    <span>{{ template.rubricConfig?.dimensions?.length ?? 0 }} dim</span>
                    <span class="meta-sep">&middot;</span>
                    <span>{{ getTemplateIndicatorCount(template) }} ind</span>
                    <span class="template-passing">min {{ template.passingScore }}/10</span>
                  </div>
                </div>

                <!-- Dimensions -->
                <div class="template-dims">
                  <details
                    v-for="dim in (template.rubricConfig?.dimensions ?? [])"
                    :key="dim.key"
                    class="dim-detail"
                  >
                    <summary class="dim-summary">
                      <div
                        class="dim-weight-bar"
                        :style="{ width: Math.max(dim.weight * 0.8, 8) + 'px' }"
                      />
                      <span class="dim-label">{{ dim.label }}</span>
                      <span class="dim-weight-pct">{{ dim.weight }}%</span>
                      <q-icon name="expand_more" size="10px" class="dim-expand-icon" />
                    </summary>
                    <div class="dim-indicators">
                      <div
                        v-for="(ind, idx) in dim.indicators"
                        :key="ind.key"
                        class="dim-indicator"
                      >
                        <span class="dim-ind-num">{{ idx + 1 }}</span>
                        <span
                          :style="{ color: ind.label.includes('ELIMINATORIO') ? '#ef4444' : 'var(--oa-text-muted)' }"
                        >
                          {{ ind.label.replace(/\s*\(ELIMINATORIO\)/, '') }}
                          <span v-if="ind.label.includes('ELIMINATORIO')" style="color: #ef4444;"> *</span>
                        </span>
                      </div>
                    </div>
                  </details>
                </div>

                <!-- Actions -->
                <div class="template-actions">
                  <router-link
                    :to="`/evaluations/score/${template.id}`"
                    class="template-action-primary"
                  >
                    <q-icon name="play_arrow" size="14px" /> Evaluar
                  </router-link>
                  <router-link
                    :to="`/evaluations/submit/${template.id}`"
                    class="template-action-secondary"
                  >
                    Entregar
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Recent Activity -->
        <section v-if="recentSubmissions.length > 0" class="recent-section">
          <h3 class="recent-title">Actividad reciente</h3>
          <div class="recent-list">
            <router-link
              v-for="sub in recentSubmissions"
              :key="sub.id"
              :to="`/evaluations/review/${sub.id}`"
              class="recent-row"
            >
              <div class="recent-info">
                <p class="recent-name">
                  {{ sub.template?.name ?? 'Entrega' }}
                  <span class="recent-attempt">#{{ sub.attemptNumber }}</span>
                </p>
                <p class="recent-date">
                  {{ formatDate(sub.submittedAt) }}
                </p>
              </div>
              <span
                v-if="getLatestScore(sub)"
                class="recent-score"
              >
                {{ getLatestScore(sub) }}
              </span>
              <q-badge
                :color="statusColor(sub.status)"
                :label="statusLabel(sub.status)"
                class="recent-badge"
              />
            </router-link>
          </div>
        </section>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { api } from 'boot/axios';

// --- Types ---
interface RubricIndicator {
  key: string;
  label: string;
  description: string;
  maxScore: number;
}

interface RubricDimension {
  key: string;
  label: string;
  weight: number;
  indicators: RubricIndicator[];
}

interface RubricConfig {
  dimensions: RubricDimension[];
}

interface EvalTemplate {
  id: string;
  name: string;
  description: string | null;
  rubricConfig: RubricConfig;
  passingScore: number;
  maxAttempts: number;
  isActive: boolean;
  course?: { title: string } | null;
  unit?: { title: string; orderIndex: number } | null;
}

interface Evaluation {
  id: string;
  evaluatorType: string;
  weightedScore: string;
  evaluatedAt: string;
  feedback: string | null;
}

interface Submission {
  id: string;
  submissionType: string;
  status: string;
  attemptNumber: number;
  submittedAt: string;
  template?: { name: string } | null;
  evaluations?: Evaluation[];
}

interface InstrumentArea {
  id: string;
  name: string;
  weight: string;
  criteria: { id: string }[];
}

// --- Constants ---
const INSTRUMENTS = [
  { key: 'live_observation', label: 'Observacion en Vivo', icon: 'visibility', scoring: '1-5', weight: '35%' },
  { key: 'performance_binary', label: 'Desempeno Operativo', icon: 'fact_check', scoring: 'Si/No', weight: '25%' },
  { key: 'protocol_checklist', label: 'Adherencia a Protocolos', icon: 'verified', scoring: 'Si/No', weight: '20%' },
  { key: 'routine_execution', label: 'Ejecucion de Rutinas', icon: 'list_alt', scoring: 'Si/No', weight: '20%' },
];

const flowSteps = ['Coach', 'Observacion', 'Desempeno', 'Protocolos', 'Rutinas', 'CNAP'];

const quickLinks = [
  { href: '/evaluations/instruments', label: 'Instrumentos', icon: 'assessment', accent: 'var(--oa-gold-full)' },
  { href: '/evaluations/history', label: 'Mi Historial', icon: 'history', accent: 'var(--oa-tier-sc)' },
  { href: '/evaluations/evolution/me', label: 'Tendencias', icon: 'trending_up', accent: 'var(--oa-tier-c1)' },
];

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  in_review: 'En revision',
  reviewed: 'Revisada',
  revision_requested: 'Correccion',
  approved: 'Aprobada',
  rejected: 'Rechazada',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'amber',
  in_review: 'blue',
  reviewed: 'teal',
  revision_requested: 'orange',
  approved: 'green',
  rejected: 'red',
};

// --- State ---
const loading = ref(true);
const error = ref<string | null>(null);
const templates = ref<EvalTemplate[]>([]);
const recentSubmissions = ref<Submission[]>([]);
const instrumentStats = ref<Record<string, { areas: number; criteria: number }>>({});
const instruments = INSTRUMENTS;

// --- Computed ---
const totalInstrumentCriteria = computed(() =>
  Object.values(instrumentStats.value).reduce((s, v) => s + v.criteria, 0),
);

const totalRubricIndicators = computed(() =>
  templates.value.reduce(
    (s, t) =>
      s +
      (t.rubricConfig?.dimensions ?? []).reduce(
        (ds: number, d: RubricDimension) => ds + d.indicators.length,
        0,
      ),
    0,
  ),
);

// --- Helpers ---
function getTemplateIndicatorCount(template: EvalTemplate): number {
  return (template.rubricConfig?.dimensions ?? []).reduce(
    (s: number, d: RubricDimension) => s + d.indicators.length,
    0,
  );
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

function statusColor(status: string): string {
  return STATUS_COLORS[status] ?? 'grey';
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function getLatestScore(sub: Submission): string | null {
  const evals = sub.evaluations ?? [];
  if (evals.length === 0) return null;
  const sorted = [...evals].sort(
    (a, b) => new Date(b.evaluatedAt).getTime() - new Date(a.evaluatedAt).getTime(),
  );
  return parseFloat(sorted[0].weightedScore).toFixed(1);
}

// --- Data fetch ---
async function fetchData() {
  loading.value = true;
  error.value = null;

  try {
    const [templatesRes, submissionsRes, instrumentsRes] = await Promise.all([
      api.get<EvalTemplate[]>('/evaluations'),
      api.get<Submission[]>('/evaluations/submissions/me'),
      api.get<Record<string, InstrumentArea[]>>('/supervision/instruments'),
    ]);

    templates.value = templatesRes.data;
    recentSubmissions.value = (submissionsRes.data ?? []).slice(0, 6);

    // Calculate instrument stats
    const stats: Record<string, { areas: number; criteria: number }> = {};
    for (const [key, areas] of Object.entries(instrumentsRes.data ?? {})) {
      const areaList = areas as InstrumentArea[];
      stats[key] = {
        areas: areaList.length,
        criteria: areaList.reduce((s, a) => s + (a.criteria?.length ?? 0), 0),
      };
    }
    instrumentStats.value = stats;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error al cargar datos';
    error.value = message;
  } finally {
    loading.value = false;
  }
}

onMounted(fetchData);
</script>

<style lang="scss" scoped>
.eval-hub-page {
  display: flex;
  justify-content: center;
  padding: 24px 16px;
}

.eval-hub-container {
  max-width: 1080px;
  width: 100%;
}

// --- Header ---
.eval-hub-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 32px;
}

.eval-hub-title {
  font-size: 30px;
  font-weight: 600;
  font-style: italic;
  font-family: var(--oa-font-display);
  color: var(--oa-text-primary);
  margin: 0;
  letter-spacing: -0.02em;
}

.eval-hub-subtitle {
  font-size: 13px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
  max-width: 480px;
}

.eval-hub-cta {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
  box-shadow: var(--oa-glow-gold);
  text-decoration: none;
  transition: transform 0.15s var(--oa-ease);
  white-space: nowrap;

  &:hover {
    transform: scale(1.02);
  }
}

// --- Grid ---
.eval-hub-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

@media (min-width: 1024px) {
  .eval-hub-grid {
    grid-template-columns: 3fr 2fr;
  }
}

// --- Section header ---
.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.section-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.section-desc {
  font-size: 11px;
  color: var(--oa-text-muted);
  margin: 0;
}

// --- Flow bar ---
.flow-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 8px;
  padding: 10px 16px;
  margin-bottom: 12px;
}

.flow-step {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
}

.flow-step-num {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
}

.flow-step-label {
  display: none;
}

@media (min-width: 640px) {
  .flow-step-label {
    display: inline;
  }
}

// --- Instruments list ---
.instrument-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.instrument-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  border-radius: 12px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  text-decoration: none;
  color: inherit;
  transition: all 0.15s var(--oa-ease);

  &:hover {
    border-color: var(--oa-gold-dim);
  }

  &:hover .instrument-label {
    color: var(--oa-gold-full);
  }

  &:hover .instrument-chevron {
    opacity: 1;
  }
}

.instrument-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
  color: var(--oa-gold-dim);
  flex-shrink: 0;
}

.instrument-info {
  flex: 1;
  min-width: 0;
}

.instrument-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--oa-text-primary);
  transition: color 0.15s;
  margin: 0;
}

.instrument-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 2px;

  span {
    font-size: 10px;
    color: var(--oa-text-disabled);
  }
}

.instrument-weight {
  font-size: 10px;
  font-family: var(--oa-font-mono);
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 4px;
  background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
  color: var(--oa-gold-dim);
}

.instrument-chevron {
  color: var(--oa-gold-full);
  opacity: 0;
  transition: opacity 0.15s;
}

// --- Quick links ---
.quick-links {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.quick-link {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  text-decoration: none;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--oa-gold-dim);
  }

  &:hover .quick-link-label {
    color: var(--oa-gold-full);
  }
}

.quick-link-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--oa-text-secondary);
  transition: color 0.15s;
}

// --- Template cards ---
.template-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.template-card {
  border-radius: 16px;
  overflow: hidden;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
}

.template-card-header {
  padding: 16px;
  border-bottom: 1px solid var(--oa-border);
}

.template-card-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.template-card-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;

  span {
    font-size: 10px;
    color: var(--oa-text-muted);
  }
}

.meta-sep {
  color: var(--oa-text-disabled) !important;
}

.template-passing {
  font-family: var(--oa-font-mono);
  color: var(--oa-text-disabled) !important;
}

// --- Dimension details ---
.template-dims {
  padding: 12px 16px;
}

.dim-detail {
  margin-bottom: 4px;

  &[open] .dim-expand-icon {
    transform: rotate(180deg);
  }
}

.dim-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  cursor: pointer;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }
}

.dim-weight-bar {
  height: 4px;
  border-radius: 2px;
  background: var(--oa-gold-dim);
  flex-shrink: 0;
}

.dim-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--oa-text-secondary);
  flex: 1;
}

.dim-weight-pct {
  font-size: 9px;
  font-family: var(--oa-font-mono);
  color: var(--oa-text-disabled);
}

.dim-expand-icon {
  color: var(--oa-text-disabled);
  transition: transform 0.2s;
}

.dim-indicators {
  padding: 4px 0 8px 16px;
}

.dim-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 9px;
  padding: 1px 0;
}

.dim-ind-num {
  font-family: var(--oa-font-mono);
  width: 10px;
  text-align: right;
  color: var(--oa-text-disabled);
}

// --- Template actions ---
.template-actions {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--oa-border);
}

.template-action-primary {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
  text-decoration: none;
  transition: transform 0.15s;

  &:hover {
    transform: scale(1.01);
  }
}

.template-action-secondary {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  background: var(--oa-bg-3);
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: color 0.15s;

  &:hover {
    color: var(--oa-text-primary);
  }
}

// --- Recent section ---
.recent-section {
  margin-top: 32px;
}

.recent-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--oa-text-secondary);
  margin: 0 0 12px;
}

.recent-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.recent-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-radius: 8px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--oa-gold-dim);
  }

  &:hover .recent-name {
    color: var(--oa-gold-full);
  }
}

.recent-info {
  flex: 1;
  min-width: 0;
}

.recent-name {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-text-primary);
  transition: color 0.15s;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.recent-attempt {
  font-weight: 400;
  color: var(--oa-text-disabled);
  margin-left: 6px;
}

.recent-date {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 0;
}

.recent-score {
  font-size: 12px;
  font-family: var(--oa-font-mono);
  font-weight: 700;
  color: var(--oa-gold-full);
}

.recent-badge {
  font-size: 10px;
}

// --- Mobile ---
@media (max-width: 640px) {
  .eval-hub-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .eval-hub-cta {
    width: 100%;
    justify-content: center;
  }

  .quick-links {
    grid-template-columns: 1fr;
  }
}
</style>
